import type { ApiResponse, ApiErrorResponse } from '@pm/shared';
import { logger } from '../lib/logger';
import { trackRequestStart, trackRequestEnd } from '../lib/correlation';

/** Maps backend error codes to i18n keys in common.errors namespace */
const ERROR_CODE_MAP: Record<string, string> = {
  NOT_FOUND: 'errors.not_found',
  VALIDATION_ERROR: 'errors.validation',
  UNAUTHORIZED: 'errors.unauthorized',
  FORBIDDEN: 'errors.forbidden',
  CONFLICT: 'errors.conflict',
  RATE_LIMITED: 'errors.rate_limited',
  INTERNAL_ERROR: 'errors.internal',
  UPSTREAM_UNAVAILABLE: 'errors.upstream',
  TENANT_NOT_FOUND: 'errors.tenant_not_found',
  TIMEOUT: 'errors.timeout',
};

let _i18n: { t: (key: string, options?: Record<string, unknown>) => string } | null = null;

function getI18n() {
  if (!_i18n) {
    try {
      // Lazy import to avoid module initialization issues in test environment
      _i18n = (globalThis as Record<string, unknown>).__pm_i18n as typeof _i18n ?? null;
    } catch { /* ignore */ }
  }
  return _i18n;
}

/** Call this from i18n/index.ts after init to register the i18n instance */
export function registerI18nForApiClient(i18n: { t: (key: string, options?: Record<string, unknown>) => string }): void {
  _i18n = i18n;
}

function resolveErrorMessage(code: string, fallbackMessage: string, status: number): string {
  const i18n = getI18n();
  if (!i18n) return fallbackMessage;

  const t = (key: string) => i18n.t(key, { ns: 'common' }) as string;

  // 403 ist immer eine Abweisung wegen fehlender Berechtigung — unabhaengig davon,
  // welchen Code das Backend mitschickt (haeufig UNKNOWN oder ein abweichender Code).
  // Der Nutzer soll hier den Berechtigungshinweis sehen, nicht eine technische Meldung.
  if (status === 403) {
    const forbidden = t('errors.forbidden');
    if (forbidden !== 'errors.forbidden') return forbidden;
  }

  const i18nKey = ERROR_CODE_MAP[code];
  if (i18nKey) {
    const translated = t(i18nKey);
    if (translated !== i18nKey) return translated;
  }
  if (status === 502 || status === 503) return t('errors.upstream');
  if (status === 408) return t('errors.timeout');
  if (status >= 500) return t('errors.internal');
  return fallbackMessage;
}

/** Event-Name fuer abgelaufene/ungueltige Sessions. AuthProvider hoert hierauf und
 *  loest Logout + Redirect zur Login-Seite aus. */
export const SESSION_EXPIRED_EVENT = 'pm:session-expired';

let sessionExpiredNotified = false;

function notifySessionExpired(): void {
  if (sessionExpiredNotified) return;
  sessionExpiredNotified = true;
  try {
    localStorage.removeItem('pm_token');
    // pm_tenant bleibt erhalten — AuthProvider braucht es fuer den naechsten Login
    sessionStorage.setItem('pm_logout_reason', 'session-expired');
  } catch { /* ignore */ }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
  }
}

/** Event-Name fuer eine veraltete/ungueltige Mandanten-ID. Der BFF weist Calls mit
 *  einem ihm unbekannten X-Tenant-Id mit 400 TENANT_NOT_FOUND ab — das passiert,
 *  wenn die gespeicherte pm_tenant auf einen Mandanten zeigt, den es (nach einer
 *  Migration/Umgebungswechsel) nicht mehr gibt. AuthProvider hoert hierauf und
 *  heilt die Wahl selbst (gueltigen Mandanten waehlen), statt dass ein Nutzer den
 *  Browser-Speicher leeren muss. Der BFF ist die autoritative Quelle — deshalb
 *  reagieren wir hierauf und ueberschreiben pm_tenant NICHT spekulativ beim Login
 *  (das bräche den T-087-Schutz gegen einen kurzzeitig unvollstaendigen Serverstand). */
export const TENANT_INVALID_EVENT = 'pm:tenant-invalid';

let tenantInvalidNotified = false;

function notifyTenantInvalid(): void {
  // Once-Guard: viele parallele Datencalls schlagen gemeinsam fehl — nur EIN Event.
  // Nach einem Reload ist das Modul frisch, der Guard also wieder offen.
  if (tenantInvalidNotified) return;
  tenantInvalidNotified = true;
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(TENANT_INVALID_EVENT));
  }
}

/** Test-Helfer: erlaubt es Tests, den One-Shot-Guard zwischen Faellen zurueckzusetzen. */
export function __resetSessionExpiredGuard(): void {
  sessionExpiredNotified = false;
}

/**
 * Token-Refresher-Hook (von api/keycloak.ts registriert). Bei einem 401 fordert
 * der apiClient EINMAL einen frischen Token an (forced updateToken) und wiederholt
 * den Request. Liefert den neuen Token (String) oder null, wenn kein Refresh
 * moeglich war. Ohne registrierten Refresher (z.B. Mock-Modus) bleibt es beim
 * bisherigen Verhalten (direkt Session-Expired).
 */
let tokenRefresher: (() => Promise<string | null>) | null = null;
export function setTokenRefresher(fn: (() => Promise<string | null>) | null): void {
  tokenRefresher = fn;
}

class ApiClient {
  private getHeaders(correlationId?: string): Record<string, string> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const token = localStorage.getItem('pm_token');
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const tenantId = localStorage.getItem('pm_tenant');
    if (tenantId) headers['X-Tenant-Id'] = tenantId;
    if (correlationId) headers['X-Correlation-ID'] = correlationId;
    return headers;
  }

  async request<T>(method: string, path: string, body?: unknown, query?: Record<string, unknown>, isRetry = false): Promise<T> {
    let url = path;
    if (query) {
      const params = new URLSearchParams();
      for (const [k, v] of Object.entries(query)) {
        if (v !== undefined && v !== null) params.set(k, String(v));
      }
      const qs = params.toString();
      if (qs) url += `?${qs}`;
    }

    const tracker = trackRequestStart(method, path);
    logger.debug(
      'api',
      `▶ ${method} ${url}`,
      { method, path, url, body: body as Record<string, unknown> | undefined, query },
      tracker.correlationId,
    );

    let resp: Response;
    try {
      resp = await fetch(url, {
        method,
        headers: this.getHeaders(tracker.correlationId),
        body: body ? JSON.stringify(body) : undefined,
        // Wichtig: BFF setzt etag-Header. Ohne cache:'no-store' liefert der
        // Browser bei 304 den ALTEN cached Body — Stand v0.13.x koennte
        // weiterleben. API-Daten muessen immer frisch vom Server kommen.
        cache: 'no-store',
      });
    } catch (networkErr) {
      const duration = trackRequestEnd(tracker.correlationId);
      const message = networkErr instanceof Error ? networkErr.message : String(networkErr);
      logger.error(
        'api',
        `✗ network error after ${duration}ms`,
        { method, path, url, error: message, duration },
        tracker.correlationId,
      );
      throw networkErr;
    }

    if (!resp.ok) {
      const duration = trackRequestEnd(tracker.correlationId);
      const errorData = (await resp.json().catch(() => ({}))) as ApiErrorResponse;
      const code = errorData.error?.code || 'UNKNOWN';
      const rawMessage = errorData.error?.message || `HTTP ${resp.status}`;
      logger.error(
        'api',
        `✗ ${resp.status} ${code}`,
        {
          method,
          path,
          url,
          status: resp.status,
          code,
          message: rawMessage,
          details: errorData.error?.details,
          duration,
        },
        tracker.correlationId,
      );

      if (resp.status === 401 && !path.includes('/auth/login')) {
        // Einmalig Token erneuern (keycloak-js forced refresh) und Request wiederholen.
        if (!isRetry && tokenRefresher) {
          const refreshed = await tokenRefresher();
          if (refreshed) return this.request<T>(method, path, body, query, true);
        }
        notifySessionExpired();
      }

      // Veraltete/ungueltige Mandanten-ID -> Selbstheilung im AuthProvider anstossen.
      if (resp.status === 400 && code === 'TENANT_NOT_FOUND') {
        notifyTenantInvalid();
      }

      throw new ApiError(
        code,
        resolveErrorMessage(code, rawMessage, resp.status),
        resp.status,
        errorData.error?.details,
      );
    }

    const duration = trackRequestEnd(tracker.correlationId);
    logger.info(
      'api',
      `✓ ${resp.status} in ${duration}ms`,
      { method, path, url, status: resp.status, duration },
      tracker.correlationId,
    );
    if (duration > 1000) {
      logger.warn(
        'perf',
        `slow request ${duration}ms`,
        { method, path, url, status: resp.status, duration },
        tracker.correlationId,
      );
    }

    if (resp.status === 204) return {} as T;
    return (await resp.json()) as T;
  }

  get<T>(path: string, query?: Record<string, unknown>) {
    return this.request<ApiResponse<T>>('GET', path, undefined, query);
  }

  post<T>(path: string, body?: unknown) {
    return this.request<ApiResponse<T>>('POST', path, body);
  }

  patch<T>(path: string, body?: unknown) {
    return this.request<ApiResponse<T>>('PATCH', path, body);
  }

  put<T>(path: string, body?: unknown) {
    return this.request<ApiResponse<T>>('PUT', path, body);
  }

  delete(path: string) {
    return this.request('DELETE', path);
  }

  /**
   * Multipart-Upload (FormData). Kein JSON-Content-Type — der Browser setzt die
   * multipart-boundary selbst. Auth/Tenant-Header wie ueblich.
   */
  async postForm<T>(path: string, form: FormData): Promise<ApiResponse<T>> {
    const tracker = trackRequestStart('POST', path);
    const headers: Record<string, string> = {};
    const token = localStorage.getItem('pm_token');
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const tenantId = localStorage.getItem('pm_tenant');
    if (tenantId) headers['X-Tenant-Id'] = tenantId;
    headers['X-Correlation-ID'] = tracker.correlationId;

    let resp: Response;
    try {
      resp = await fetch(path, { method: 'POST', headers, body: form });
    } catch (networkErr) {
      trackRequestEnd(tracker.correlationId);
      throw networkErr;
    }
    if (!resp.ok) {
      trackRequestEnd(tracker.correlationId);
      const errorData = (await resp.json().catch(() => ({}))) as ApiErrorResponse;
      const code = errorData.error?.code || 'UNKNOWN';
      if (resp.status === 401 && !path.includes('/auth/login')) notifySessionExpired();
      throw new ApiError(code, resolveErrorMessage(code, errorData.error?.message || `HTTP ${resp.status}`, resp.status), resp.status, errorData.error?.details);
    }
    trackRequestEnd(tracker.correlationId);
    if (resp.status === 204) return {} as ApiResponse<T>;
    return (await resp.json()) as ApiResponse<T>;
  }

  /** Blob-Request für Datei-Downloads (CSV, ZIP). Kein JSON-Parsing. */
  async requestBlob(method: string, path: string, body?: unknown): Promise<Blob> {
    const tracker = trackRequestStart(method, path);
    logger.debug(
      'api',
      `▶ ${method} ${path} (blob)`,
      { method, path, body: body as Record<string, unknown> | undefined, blob: true },
      tracker.correlationId,
    );

    const headers: Record<string, string> = {};
    const token = localStorage.getItem('pm_token');
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const tenantId = localStorage.getItem('pm_tenant');
    if (tenantId) headers['X-Tenant-Id'] = tenantId;
    if (body) headers['Content-Type'] = 'application/json';
    headers['X-Correlation-ID'] = tracker.correlationId;

    let resp: Response;
    try {
      resp = await fetch(path, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
      });
    } catch (networkErr) {
      const duration = trackRequestEnd(tracker.correlationId);
      const message = networkErr instanceof Error ? networkErr.message : String(networkErr);
      logger.error(
        'api',
        `✗ network error after ${duration}ms (blob)`,
        { method, path, error: message, duration, blob: true },
        tracker.correlationId,
      );
      throw networkErr;
    }

    if (!resp.ok) {
      const duration = trackRequestEnd(tracker.correlationId);
      const text = await resp.text().catch(() => '');
      logger.error(
        'api',
        `✗ ${resp.status} BLOB_REQUEST_FAILED`,
        { method, path, status: resp.status, code: 'BLOB_REQUEST_FAILED', message: text, duration },
        tracker.correlationId,
      );
      if (resp.status === 401 && !path.includes('/auth/login')) {
        notifySessionExpired();
      }
      throw new ApiError(
        'BLOB_REQUEST_FAILED',
        text || `Blob request failed: ${resp.status}`,
        resp.status,
      );
    }

    const duration = trackRequestEnd(tracker.correlationId);
    logger.info(
      'api',
      `✓ ${resp.status} in ${duration}ms (blob)`,
      { method, path, status: resp.status, duration, blob: true },
      tracker.correlationId,
    );
    if (duration > 1000) {
      logger.warn(
        'perf',
        `slow request ${duration}ms (blob)`,
        { method, path, status: resp.status, duration, blob: true },
        tracker.correlationId,
      );
    }
    return resp.blob();
  }
}

export class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public status: number,
    public details?: unknown[],
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Formatiert ApiError-Details (z.B. Zod-Validation-Issues) zu einem
 * User-lesbaren mehrzeiligen String. Beispiele:
 *   "Daten ungültig: zeiten_flexibility — Invalid enum value …"
 *   "Daten ungültig: 2 Felder betroffen (zeiten_flexibility, zeiten_budget_flex)"
 */
export function formatApiError(err: unknown): string {
  if (!(err instanceof ApiError)) {
    return err instanceof Error ? err.message : String(err);
  }
  const details = err.details;
  if (!Array.isArray(details) || details.length === 0) return err.message;
  const issues = details
    .map((d) => {
      const it = d as { field?: string; message?: string };
      if (it.field && it.message) return `${it.field}: ${it.message}`;
      if (it.message) return it.message;
      return null;
    })
    .filter((s): s is string => !!s);
  if (issues.length === 0) return err.message;
  if (issues.length === 1) return `${err.message} — ${issues[0]}`;
  return `${err.message}\n• ${issues.join('\n• ')}`;
}

export const apiClient = new ApiClient();
